import { useState } from 'react';
import { C } from '../constants';
import { toDay, toTime, fmtDay } from '../utils';
import { Card, SLabel } from '../components/Atoms';
import { sendPush } from '../push';

const COMMON_SERVICES = [
  'Cardiology','Pulmonology','Nephrology','Gastroenterology','Neurology',
  'Endocrinology','Hematology','Oncology','Rheumatology','Infectious Disease',
  'General Surgery','Orthopedics','Urology','Neurosurgery','Vascular Surgery',
  'Psychiatry','Palliative Care','Physical Therapy','Social Work','Other',
];

const SERVICE_COLOR = '#4A9EFF';

const STATUS_STYLE = {
  'Pending':  { color: '#FF9500', bg: '#FF950022', border: '#FF950044' },
  'Received': { color: '#BF7FFF', bg: '#BF7FFF22', border: '#BF7FFF44' },
  'Seen':     { color: '#30D158', bg: '#30D15822', border: '#30D15844' },
};

// ── Main ReferTab ─────────────────────────────────────────────────────────
export default function ReferTab({ cur, patch, saveReferral, changeReferStatus, shiftCode }) {
  console.log('🔴 ReferTab v2 loaded — shiftCode:', shiftCode, 'sendPush available:', typeof sendPush);
  const [service,   setService]   = useState('');
  const [custom,    setCustom]    = useState('');
  const [date,      setDate]      = useState(() => toDay());
  const [time,      setTime]      = useState(() => toTime());
  const [reason,    setReason]    = useState('');
  const [status,    setStatus]    = useState('Pending');
  const [editingId, setEditingId] = useState(null);
  const [editInput, setEditInput] = useState('');
  const [showForm,  setShowForm]  = useState(false);

  const referLog = cur.referLog || [];
  const activeService = service === 'Other' ? custom.trim() : service;

  const save = () => {
    if (!activeService || !reason.trim()) return;
    const entry = {
      id: `ref${Date.now()}`,
      service: activeService,
      date, time,
      reason: reason.trim(),
      consultInput: '',
      status,
    };
    patch(p => ({ ...p, referLog: [...(p.referLog || []), entry] }));
    if (shiftCode) sendPush(shiftCode, '📋 New Referral', (cur.name||'Patient') + (cur.room ? ' · Rm '+cur.room : '') + ' → ' + activeService + ' (' + status + ')', cur);
    setReason(''); setStatus('Pending');
  };

  const del = (id) => {
    if (!window.confirm('Delete this referral note?')) return;
    patch(p => ({ ...p, referLog: (p.referLog || []).filter(e => e.id !== id) }));
  };

  const updateStatus = (id, newStatus) => {
    const entry = (cur.referLog || []).find(e => e.id === id);
    if (!entry || entry.status === newStatus) return;
    patch(p => ({ ...p, referLog: (p.referLog || []).map(e => e.id === id ? { ...e, status: newStatus } : e) }));
    if (shiftCode) sendPush(shiftCode, '🔄 Referral Update', (cur.name||'Patient') + (cur.room ? ' · Rm '+cur.room : '') + ' — ' + entry.service + ': ' + newStatus, cur);
  };

  const saveConsultInput = (id) => {
    patch(p => ({ ...p, referLog: (p.referLog || []).map(e => e.id === id ? { ...e, consultInput: editInput } : e) }));
    setEditingId(null);
  };

  // Group by service, newest first within each group
  const grouped = referLog.reduce((acc, e) => {
    if (!acc[e.service]) acc[e.service] = [];
    acc[e.service].push(e);
    return acc;
  }, {});
  Object.keys(grouped).forEach(svc => {
    grouped[svc].sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
  });

  // Sort services by their most recent entry (newest service group first)
  const sortedServices = Object.entries(grouped).sort(([, a], [, b]) =>
    (b[0].date + b[0].time).localeCompare(a[0].date + a[0].time)
  );

  const inputStyle = {
    width: '100%', background: C.bg3, border: `1px solid ${C.bd}`,
    borderRadius: 8, padding: '8px 10px', color: C.tx, fontSize: 13,
  };

  return (
    <>
      {!showForm && (
        <button onClick={() => { setShowForm(true); setService(''); setCustom(''); setReason(''); setStatus('Pending'); setDate(toDay()); setTime(toTime()); }} style={{
          width: '100%', padding: '11px 0', borderRadius: 10, border: '1px dashed #2A4A6A',
          background: 'transparent', color: '#4A9EFF', fontSize: 13, fontWeight: 700,
          cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
          ＋ New Referral
        </button>
      )}

      {showForm && (
      <Card s={{ border: '1px solid #2A4A6A' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <SLabel color={SERVICE_COLOR} mb={0}>New Referral</SLabel>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '4px 6px', color: C.tx, fontSize: 11 }} />
            <input type="time" value={time} onChange={e => setTime(e.target.value)}
              style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '4px 6px', color: C.tx, fontSize: 11 }} />
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Service</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
            {COMMON_SERVICES.map(s => {
              const active = service === s;
              return (
                <button key={s} onClick={() => setService(s)}
                  style={{ padding: '4px 10px', borderRadius: 8, border: `1px solid ${active ? SERVICE_COLOR : C.bd}`, background: active ? '#1A3D6A' : 'transparent', color: active ? '#7DC4FF' : C.dim, fontSize: 11, fontWeight: active ? 700 : 400, cursor: 'pointer' }}>
                  {s}
                </button>
              );
            })}
          </div>
          {service === 'Other' && (
            <input value={custom} onChange={e => setCustom(e.target.value)} placeholder="Enter service name…"
              style={{ ...inputStyle, marginTop: 4 }} />
          )}
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Status</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {Object.entries(STATUS_STYLE).map(([s, st]) => (
              <button key={s} onClick={() => setStatus(s)}
                style={{ flex: 1, padding: '6px 0', borderRadius: 8, border: `1px solid ${status === s ? st.border : C.bd}`, background: status === s ? st.bg : 'transparent', color: status === s ? st.color : C.dim, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Reason for Referral</div>
          <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Clinical question, reason for consult…" rows={3}
            style={{ ...inputStyle, resize: 'vertical' }} />
        </div>

        <button onClick={save} disabled={!activeService || !reason.trim()}
          style={{ width: '100%', padding: '10px 0', borderRadius: 10, background: activeService && reason.trim() ? '#1A3D6A' : C.bg2, border: 'none', color: activeService && reason.trim() ? '#7DC4FF' : C.dim, fontSize: 13, fontWeight: 700, cursor: activeService && reason.trim() ? 'pointer' : 'default' }}>
          💾 Save Referral
        </button>
        <button onClick={() => { setShowForm(false); setService(''); setCustom(''); setReason(''); setStatus('Pending'); setDate(toDay()); setTime(toTime()); }}
          style={{ width: '100%', marginTop: 6, padding: '7px 0', borderRadius: 10, background: 'transparent', border: 'none', color: '#3A5A7A', fontSize: 12, cursor: 'pointer' }}>
          ✕ Cancel
        </button>
      </Card>
      )}

      {sortedServices.length === 0 && (
        <div style={{ textAlign: 'center', color: C.dim2, fontSize: 13, padding: '30px 0' }}>No referrals recorded</div>
      )}

      {sortedServices.map(([svc, entries]) => {
        const latestStatus = entries[0]?.status || 'Pending'; // eslint-disable-line no-unused-vars
        return (
          <div key={svc} style={{ marginBottom: 16 }}>
            {/* Service header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: SERVICE_COLOR, background: '#1A3D6A', border: '1px solid #4A9EFF44', borderRadius: 8, padding: '4px 12px', flex: 1 }}>{svc}</div>
              <div style={{ fontSize: 10, color: C.dim2 }}>{entries.length} note{entries.length !== 1 ? 's' : ''}</div>
            </div>

            {entries.map(e => {
              const isEditing = editingId === e.id;
              return (
                <div key={e.id} style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderLeft: `3px solid ${SERVICE_COLOR}`, borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 10, color: C.dim }}>{fmtDay(e.date)} · {e.time}</div>
                    </div>

                    <button onClick={() => del(e.id)} style={{ background: 'none', border: 'none', color: C.dim2, cursor: 'pointer', fontSize: 16, padding: 0 }}>×</button>
                  </div>

                  {/* Reason */}
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>Reason for Referral</div>
                    <div style={{ fontSize: 13, color: C.tx, lineHeight: 1.6 }}>{e.reason}</div>
                  </div>

                  {/* Consult input */}
                  <div style={{ background: C.bg2, borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: isEditing ? 6 : 0 }}>
                      <div style={{ fontSize: 9, fontWeight: 800, color: '#30D158', textTransform: 'uppercase', letterSpacing: '0.08em', flex: 1 }}>Service Input / Recommendations</div>
                      {!isEditing && (
                        <button onClick={() => { setEditingId(e.id); setEditInput(e.consultInput || ''); }}
                          style={{ background: 'none', border: '1px solid #2A4A2A', borderRadius: 6, color: '#30D158', fontSize: 10, padding: '2px 8px', cursor: 'pointer' }}>
                          {e.consultInput ? '✏ Edit' : '+ Add'}
                        </button>
                      )}
                    </div>
                    {!isEditing && e.consultInput && (
                      <div style={{ fontSize: 12, color: '#A0D8A8', lineHeight: 1.6, marginTop: 4 }}>{e.consultInput}</div>
                    )}
                    {!isEditing && !e.consultInput && (
                      <div style={{ fontSize: 11, color: C.dim2, fontStyle: 'italic', marginTop: 2 }}>No input yet — tap + Add to record</div>
                    )}
                    {isEditing && (
                      <>
                        <textarea value={editInput} onChange={ev => setEditInput(ev.target.value)}
                          placeholder="Record service's findings, recommendations, plan…" rows={4} autoFocus
                          style={{ width: '100%', background: C.bg3, border: `1px solid #30D15844`, borderRadius: 8, padding: '8px 10px', color: '#A0D8A8', fontSize: 12, resize: 'vertical' }} />
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          <button onClick={() => saveConsultInput(e.id)}
                            style={{ flex: 1, background: '#30D15822', border: '1px solid #30D15844', borderRadius: 8, color: '#30D158', fontSize: 12, fontWeight: 700, padding: '6px 0', cursor: 'pointer' }}>
                            ✓ Save
                          </button>
                          <button onClick={() => setEditingId(null)}
                            style={{ background: 'none', border: `1px solid ${C.bd}`, borderRadius: 8, color: C.dim, fontSize: 12, padding: '6px 12px', cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Status */}
                  <div style={{ display: 'flex', gap: 5 }}>
                    {Object.entries(STATUS_STYLE).map(([s, sv]) => (
                      <button key={s} onClick={() => updateStatus(e.id, s)}
                        style={{ flex: 1, padding: '4px 0', borderRadius: 6, border: `1px solid ${e.status === s ? sv.border : C.bd}`, background: e.status === s ? sv.bg : 'transparent', color: e.status === s ? sv.color : C.dim2, fontSize: 10, fontWeight: e.status === s ? 700 : 400, cursor: 'pointer' }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
